import React, { FunctionComponent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Box, Button, Text } from '@wix/design-system';
import ChevronRightSmallIcon from 'wix-ui-icons-common/ChevronRightSmall';
import ChevronLeftSmallIcon from 'wix-ui-icons-common/ChevronLeftSmall';
import { Article, ArticleType } from '@wix/answers-api';
import { useTranslation } from '@wix/wix-i18n-config';
import { pageClick } from '@wix/bi-logger-new-help-center/v2';
import { isMobile } from '../../../utils/userAgent';
import { BI, CATEGORY_ARTICLES_PAGE_SIZE } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { InnerLink } from '../../InnerLink';
import { isRTL } from '../../../utils/internationalization';
import css from './index.module.scss';

type CategoryArticlesSectionProps = {
  articles: Article[];
  type: ArticleType;
  selectedCategoryId: string;
  startIndex: number;
};

export const CategoryArticlesSection: FunctionComponent<
  CategoryArticlesSectionProps
> = ({ articles, type, selectedCategoryId, startIndex }) => {
  const [inViewArticles, setInViewArticles] = useState<Article[]>([]);
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { sendBIEvent } = useBI();
  const isRTLLanguage = isRTL(locale);

  const getArticleTypeTitle = (): string => {
    switch (type) {
      case ArticleType.KNOWN_ISSUE:
        return t('category.articles.type.known.issue');
      case ArticleType.FEATURE_REQUEST:
        return t('category.articles.type.feature.requests');
      case ArticleType.ARTICLE:
      default:
        return t('category.articles.type.articles');
    }
  };

  const showMoreArticles = () => {
    setInViewArticles(
      articles.slice(0, inViewArticles.length + CATEGORY_ARTICLES_PAGE_SIZE)
    );
  };

  useEffect(() => {
    setInViewArticles(
      isMobile() ? articles.slice(0, CATEGORY_ARTICLES_PAGE_SIZE) : articles
    );
  }, [articles]);

  const onArticleClick = async (article: Article, order: number) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.CATEGORY,
        kb_lang: locale as string,
        item_id: selectedCategoryId,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.ARTICLE_SELECTION,
        clicked_url: article.url,
        clicked_text: article.title,
        clicked_item_order: `${order + startIndex}`,
        clicked_item_id: article.id,
      })
    );
  };

  return (
    <>
      {inViewArticles.length > 0 && (
        <Box direction="vertical" className={css.articles}>
          <Text
            className={css.articleType}
            dataHook={DATA_HOOKS.CATEGORY_ARTICLES_SECTION_TYPE}
          >
            {getArticleTypeTitle()}
          </Text>
          <Box direction="vertical" className={css.articleLinks}>
            {inViewArticles.map((article, index) => (
              <InnerLink
                href={article.uri}
                className={css.articleLink}
                dataHook={DATA_HOOKS.CATEGORY_ARTICLES_SECTION_LINK}
                key={article.id}
                onClick={() => onArticleClick(article, index + 1)}
              >
                <Box gap="5px" verticalAlign="middle">
                  {isRTLLanguage ? (
                    <ChevronLeftSmallIcon className={css.articleIcon} />
                  ) : (
                    <ChevronRightSmallIcon className={css.articleIcon} />
                  )}
                  <Text
                    size="medium"
                    maxLines={2}
                    ellipsis
                    disabled
                    className={css.articleTitle}
                  >
                    {article.title}
                  </Text>
                </Box>
              </InnerLink>
            ))}
          </Box>
          {articles.length > inViewArticles.length && (
            <Button
              onClick={showMoreArticles}
              priority="secondary"
              className={css.showMore}
            >
              {t('category.articles.show.more')}
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
